/**
 * Mobile dock — the fixed 5-item bottom nav in `sections/mobile-dock.liquid`, phone only.
 *
 * Rest state is off-screen (`transform: translateY(100%)`, see `component-mobile-dock.css`).
 * It reveals exactly when the sticky header hides — see the `hubcart:header:hidden` /
 * `hubcart:header:shown` events published by `component-header.js` — and at the very
 * bottom of the page, so the two never disagree about which chrome owns the screen edge.
 * That pairing is the reference's: with a sticky header its dock has no scroll-position
 * reveal of its own, only the header-hidden coupling and the footer-bottom check. The keyboard-reachability rule (any dock item taking focus reveals it) is left to
 * `:focus-within` in CSS; nothing here needs to know a dock item was tabbed to.
 *
 * Also publishes `--mobile-dock-height` on `:root`. `sections/footer.liquid` reads it for
 * `.footer__copyright`'s extra bottom padding; Agent 1's `facet-sticky` and Agent 2's
 * `product-sticky-form` are documented to read it too when they land, to translate clear
 * of the dock rather than sitting under it.
 */
(function () {
  'use strict';

  const dock = document.querySelector('[data-mobile-dock]');
  if (!dock) return;

  /* Kept from the last measurement so `update()` can ask whether the dock is on screen at
     all without forcing a second layout on every scroll frame. */
  let height = 0;

  function publishHeight() {
    height = dock.offsetHeight;
    document.documentElement.style.setProperty('--mobile-dock-height', height + 'px');
  }

  function atPageBottom() {
    const doc = document.documentElement;
    return window.scrollY + window.innerHeight >= doc.scrollHeight - 1;
  }

  /* Reveal is owned by two signals, not scroll position: the header hiding (tracked from
     its events, below) and the page bottom — where the reference also reveals its dock so
     the footer is never the only chrome on screen. */
  let headerHidden = false;

  function update() {
    /* `height` gates the whole thing on the dock being rendered: above 767px the CSS takes
       it to `display: none`, and the reference likewise never puts `.active` on its
       `mobile-dock-section` at desktop widths. Without this the root flag below would claim
       a dock is in the way of the sticky buy bar and the facet bar on every desktop page. */
    const visible = height > 0 && (headerHidden || atPageBottom());
    dock.classList.toggle('is-visible', visible);
    /* Published on the root element as well as on the dock, because the things that have
       to move out of the dock's way are not its descendants and not its siblings either:
       the sticky buy bar lives inside the product section and the facet bar inside the
       collection one. The reference reaches them with a sibling combinator from its own
       section wrapper (`.mobile-dock-section.active ~ .page-container
       .product-sticky-form`); our dock section is not a preceding sibling of those, so
       the same conditional is carried on the root instead. Conditional either way — an
       unconditional offset leaves the bar floating 61px above the screen edge whenever
       the dock is away. */
    document.documentElement.toggleAttribute('data-mobile-dock-visible', visible);
  }

  let ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      update();
      ticking = false;
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    publishHeight();
    update();
  });

  document.addEventListener('hubcart:header:hidden', () => {
    headerHidden = true;
    update();
  });
  document.addEventListener('hubcart:header:shown', () => {
    headerHidden = false;
    update();
  });

  publishHeight();
  update();

  /* The off-screen rest state must be on screen for at least one paint before the
     transition is enabled, or the dock visibly slides up the moment the page loads. Two
     nested frames — the first to let the initial paint happen, the second to enable the
     transition for every toggle after it — is the standard way to get that without a
     hardcoded delay. */
  requestAnimationFrame(() => {
    requestAnimationFrame(() => dock.setAttribute('data-loaded', ''));
  });
})();
